import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, ListGroup, Spinner } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { toast } from "react-toastify";
import axios from "axios";
import "../Styling/ProductionPlan.css";

const DURATION_UNITS = ["H", "Years", "Months", "Weeks"];
const LABOUR_TYPES = ["SUP", "MAN", "LAB"];
const LABOUR_DURATION_UNITS = ["H", "Days", "Weeks", "Months", "Years"];

const ProductionPlan = () => {
  const { user } = useAuth();
  const orgId = user?.organisationId;
  const [serviceItems,setServiceItems] = useState([]);
  const[sfItems,setSfItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLookups, setFetchingLookups] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [items, setItems] = useState([]);
  const [AllItems, setAllItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [offices, setOffices] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const [form, setForm] = useState({
    planName: "",
    itemCode: "",
    productionQuantity: "",
    productionQuantityUnit: "",
    officeCode: "",
  });

  const emptyLabour = () => ({
    labourType: LABOUR_TYPES[0],
    labourDuration: "",
    labourDurationUnit: LABOUR_DURATION_UNITS[0],
  });

  const emptyProcessItem = () => ({
    itemId: "",
    quantity: "",
    bom: null,
    bomLoading: false,
    bomError: null,
  });

  const emptySFGItem = () => ({
    itemId: "",
    quantity: "",
    unit: "",
    items: [emptyProcessItem()],
  });

  const emptySubprocessEntry = () => ({
    approxDuration: "",
    approxDurationUnit: DURATION_UNITS[0],
    labours: [emptyLabour()],
    sfgOutputs: [emptySFGItem()],
  });

  const emptyProcessEntry = () => ({
    productionProcessId: "",
    subprocessItems: {},
  });

  const [processEntries, setProcessEntries] = useState([emptyProcessEntry()]);

  useEffect(() => {
    if (!orgId) return;
    const fetchAllLookups = async () => {
      setFetchingLookups(true);
      setFetchError(null);
      try {
        const pRes = await fetch(`${Base_URL}/ProductionStation/GetAllProcesses/id?id=${encodeURIComponent(orgId)}`);
        if (!pRes.ok) throw new Error(`Failed to load processes: ${pRes.status}`);
        const pData = await pRes.json();
        const pArr = Array.isArray(pData) ? pData : [];
        setProcesses(pArr);

        const iRes = await fetch(`${Base_URL}/Item/GetByOrgId?orgId=${user?.organisationId}`);
        if (!iRes.ok) throw new Error(`Failed to load items: ${iRes.status}`);
        const iData = await iRes.json();
        const filteredItems = Array.isArray(iData) ? iData.filter(it => it.itemCode && String(it.itemCode).startsWith("RA") ||String(it.itemCode).startsWith("SM")) : [];
        const filteredServiceItems = Array.isArray(iData) ? iData.filter(it => it.itemCode && String(it.itemCode).startsWith("SR")) : [];
        const filteredSFGItems = Array.isArray(iData) ? iData.filter(it => it.itemCode && String(it.itemCode).startsWith("SM")) : [];

        setItems(filteredItems);
        setServiceItems(filteredServiceItems);
        setSfItems(filteredSFGItems);
        setAllItems(iData);

        const uRes = await fetch(`${Base_URL}/UnitMaster/GetAllUnits`);
        if (!uRes.ok) throw new Error(`Failed to load units: ${uRes.status}`);
        const uData = await uRes.json();
        const uArr = Array.isArray(uData) ? uData : [];
        setUnits(uArr);

        let officeData = [];
        try {
          const officeRes = await fetch(`${Base_URL}/Office/OfficeByOrgId/${encodeURIComponent(orgId)}`);
          if (officeRes.ok) {
            const od = await officeRes.json();
            officeData = Array.isArray(od) ? od : [];
          } else {
            const officeRes2 = await fetch(`${Base_URL}/Office/OfficeByOrgId?orgId=${encodeURIComponent(orgId)}`);
            if (officeRes2.ok) {
              const od2 = await officeRes2.json();
              officeData = Array.isArray(od2) ? od2 : [];
            }
          }
        } catch (e) {
          console.warn("Office fetch fallback attempt failed", e);
        }
        setOffices(officeData);

        setForm(prev => ({
          ...prev,
          productionQuantityUnit: uArr[0]?.unitCode ?? uArr[0]?.code ?? uArr[0]?.value ?? prev.productionQuantityUnit ?? "",
          officeCode: officeData[0]?.officeCode ?? officeData[0]?.code ?? officeData[0]?.value ?? prev.officeCode ?? "",
        }));

        if (pArr.length > 0) {
          setProcessEntries(prev => {
            const copy = [...prev];
            if (!copy[0].productionProcessId) {
              copy[0].productionProcessId = String(pArr[0].productionProcessId ?? pArr[0].productionprocessid ?? pArr[0].id ?? "");
              const subs = parseSubprocessList(pArr[0]);
              const map = {};
              subs.forEach(sid => map[sid] = emptySubprocessEntry());
              copy[0].subprocessItems = map;
            }
            return copy;
          });
        }
      } catch (err) {
        console.error(err);
        setFetchError(err.message ?? "Error loading lookups");
      } finally {
        setFetchingLookups(false);
      }
    };

    fetchAllLookups();
  }, [orgId]);

  const findProcessById = (id) => {
    if (!id) return null;
    return processes.find(p => String(p.productionProcessId ?? p.productionprocessid ?? p.id) === String(id)) ?? null;
  };

  const findItemById = (id) => {
    if (!id) return null;
    return items.find(it => String(it.itemId) === String(id)) ?? null;
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  const parseSubprocessList = (proc) => {
    const raw = proc?.subprocessList ?? proc?.subProcessList ?? "";
    if (!raw) return [];
    return String(raw).split(",").map(s => s.trim()).filter(Boolean);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addProcessEntry = () => {
    const defaultProc = processes[0]?.productionProcessId ?? processes[0]?.productionprocessid ?? processes[0]?.id ?? "";
    setProcessEntries(prev => [...prev, { ...emptyProcessEntry(), productionProcessId: defaultProc ? String(defaultProc) : "" }]);
  };

  const removeProcessEntry = (index) => {
    setProcessEntries(prev => prev.filter((_, i) => i !== index));
  };

  const updateProcessEntry = (index, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });
  };

  const updateItemInSFG = (procIndex, subId, sfgIndex, itemIndex, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const sub = copy[procIndex].subprocessItems[subId];
      const newSFGs = sub.sfgOutputs.map((s, i) => {
        if (i !== sfgIndex) return s;
        return {
          ...s,
          items: s.items.map((it, idx) =>
            idx === itemIndex ? { ...it, ...patch } : it
          ),
        };
      });
      sub.sfgOutputs = newSFGs;
      return [...copy];
    });
  };

  const onProcessSelectChange = (index, value) => {
    const proc = findProcessById(value);
    const subs = parseSubprocessList(proc || {});
    const map = {};
    subs.forEach(sid => map[sid] = emptySubprocessEntry());
    updateProcessEntry(index, { productionProcessId: value, subprocessItems: map });
  };

  const addSFGItem = (procIndex, subId) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.sfgOutputs = [...(sub.sfgOutputs || []), emptySFGItem()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const updateSFGItem = (procIndex, subId, index, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.sfgOutputs = sub.sfgOutputs.map((s, i) =>
        i === index ? { ...s, ...patch } : s
      );
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const removeSFGItem = (procIndex, subId, index) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.sfgOutputs = sub.sfgOutputs.filter((_, i) => i !== index);
      if (sub.sfgOutputs.length === 0) sub.sfgOutputs = [emptySFGItem()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const updateSubprocessField = (procIndex, subId, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      Object.assign(sub, patch);
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

 const fetchBOMForSFGItem = async (
  procIndex,
  subId,
  sfgIndex,
  itemIndex,
  itemId,
  parentItemId
) => {
  if (!itemId || !parentItemId) return;

  setProcessEntries(prev => {
    const copy = [...prev];
    const sub = copy[procIndex].subprocessItems[subId];
    sub.sfgOutputs = sub.sfgOutputs.map((s, i) => {
      if (i !== sfgIndex) return s;
      return {
        ...s,
        items: s.items.map((it, idx) =>
          idx === itemIndex ? { ...it, bomLoading: true, bomError: null } : it
        ),
      };
    });
    return [...copy];
  });

  try {
   const res = await fetch(
  `${Base_URL}/ProductionStation/GetBOMfromItem/parentItemId/childItemId?parentItemId=${encodeURIComponent(parentItemId)}&childItemId=${encodeURIComponent(itemId)}`
);


    if (!res.ok) throw new Error(`BOM fetch failed`);
    const data = await res.json();

    setProcessEntries(prev => {
      const copy = [...prev];
      const sub = copy[procIndex].subprocessItems[subId];
      sub.sfgOutputs = sub.sfgOutputs.map((s, i) => {
        if (i !== sfgIndex) return s;
        return {
          ...s,
          items: s.items.map((it, idx) =>
            idx === itemIndex ? { ...it, bom: data, bomLoading: false } : it
          ),
        };
      });
      return [...copy];
    });
  } catch (err) {
    setProcessEntries(prev => {
      const copy = [...prev];
      const sub = copy[procIndex].subprocessItems[subId];
      sub.sfgOutputs = sub.sfgOutputs.map((s, i) => {
        if (i !== sfgIndex) return s;
        return {
          ...s,
          items: s.items.map((it, idx) =>
            idx === itemIndex
              ? { ...it, bom: null, bomError: err.message, bomLoading: false }
              : it
          ),
        };
      });
      return [...copy];
    });
  }
};


  const addLabourToSubprocess = (procIndex, subId) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = [...(sub.labours || []), emptyLabour()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const removeLabourFromSubprocess = (procIndex, subId, labourIndex) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = (sub.labours || []).filter((_, i) => i !== labourIndex);
      if (!sub.labours || sub.labours.length === 0) sub.labours = [emptyLabour()];
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const updateLabourInSubprocess = (procIndex, subId, labourIndex, patch) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const entry = { ...copy[procIndex] };
      const map = { ...(entry.subprocessItems || {}) };
      const sub = { ...(map[subId] || emptySubprocessEntry()) };
      sub.labours = (sub.labours || []).map((l, i) => i === labourIndex ? { ...l, ...patch } : l);
      map[subId] = sub;
      entry.subprocessItems = map;
      copy[procIndex] = entry;
      return copy;
    });
  };

  const validate = () => {
    if (!form.planName || !form.planName.trim()) return "Enter Plan Name";
    if (!form.itemCode || !form.itemCode.trim()) return "Enter Item Code";
    if (!form.productionQuantity || isNaN(Number(form.productionQuantity))) return "Enter Production Quantity";
    if (!form.productionQuantityUnit) return "Select Production Quantity Unit";
    if (!form.officeCode) return "Select Office Code";
    if (!processEntries || processEntries.length === 0) return "Add at least one process";

    for (let i = 0; i < processEntries.length; i++) {
      const pe = processEntries[i];
      if (!pe.productionProcessId) return `Process ${i + 1}: select production process`;

      const map = pe.subprocessItems || {};
      const subIds = Object.keys(map || {});
      if (!subIds || subIds.length === 0) return `Process ${i + 1}: no subprocesses available for selected process`;

      for (let s = 0; s < subIds.length; s++) {
        const sid = subIds[s];
        const sub = map[sid] || emptySubprocessEntry();

        if (sub.approxDuration === "" || sub.approxDuration === null) return `Process ${i + 1} Subprocess ${sid}: enter approx duration`;
        if (!sub.approxDurationUnit) return `Process ${i + 1} Subprocess ${sid}: select approx duration unit`;
        if (!sub.sfgOutputs || sub.sfgOutputs.length === 0) return `Process ${i + 1} Subprocess ${sid}: add at least one SFG item`;

        for (let sf = 0; sf < sub.sfgOutputs.length; sf++) {
          const sfg = sub.sfgOutputs[sf];
          if (!sfg.itemId) return `Process ${i + 1} Subprocess ${sid}: select SFG item`;
        }

        for (let x = 0; x < sub.sfgOutputs.length; x++) {
          const sfg = sub.sfgOutputs[x];
          if (!sfg.items || sfg.items.length === 0) return `Process ${i + 1} Subprocess ${sid} SFG ${x + 1}: add items`;
          for (let y = 0; y < sfg.items.length; y++) {
            if (!sfg.items[y].itemId) return `Process ${i + 1} Subprocess ${sid} SFG ${x + 1}: select item`;
          }
        }

        if (!sub.labours || sub.labours.length === 0) return `Process ${i + 1} Subprocess ${sid}: add at least one labour`;
        for (let k = 0; k < sub.labours.length; k++) {
          const lb = sub.labours[k];
          if (!lb.labourType) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: select labour type`;
          if (lb.labourDuration === "" || lb.labourDuration === null) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: enter labour duration`;
          if (!lb.labourDurationUnit) return `Process ${i + 1} Subprocess ${sid} Labour ${k + 1}: select labour duration unit`;
        }
      }
    }
    return null;
  };

  const addItemToSFG = (procIndex, subId, sfgIndex) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const sub = copy[procIndex].subprocessItems[subId];
      const newSFGs = sub.sfgOutputs.map((s, i) =>
        i === sfgIndex
          ? { ...s, items: [...(s.items || []), emptyProcessItem()] }
          : s
      );
      sub.sfgOutputs = newSFGs;
      return [...copy];
    });
  };

  const removeItemFromSFG = (procIndex, subId, sfgIndex, itemIndex) => {
    setProcessEntries(prev => {
      const copy = [...prev];
      const sfg = copy[procIndex].subprocessItems[subId].sfgOutputs[sfgIndex];
      sfg.items = sfg.items.filter((_, i) => i !== itemIndex);
      if (sfg.items.length === 0) sfg.items = [emptyProcessItem()];
      return [...copy];
    });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      planName: form.planName.trim(),
      itemCode: form.itemCode.trim(),
      orgId,
      productionQuantity: Number(form.productionQuantity),
      productionQuantityUnit: form.productionQuantityUnit,
      officeCode: form.officeCode,
      createdBy: user?.userId,
      processes: processEntries.map(pe => {
        const procDetails = findProcessById(pe.productionProcessId);
        const subprocessArray = Object.entries(pe.subprocessItems || {}).map(([subId, sub]) => ({
          subProcessId: subId,
          approxDuration: Number(sub.approxDuration),
          approxDurationUnit: sub.approxDurationUnit,
          labours: (sub.labours || []).map(l => ({
            labourType: l.labourType,
            labourDuration: Number(l.labourDuration),
            labourDurationUnit: l.labourDurationUnit,
          })),
          sfgOutputs: (sub.sfgOutputs || []).map(s => ({
            itemId: s.itemId,
            quantity: s.quantity ? Number(s.quantity) : null,
            unit: s.unit,
            items: (s.items || []).map(it => ({
              itemId: it.itemId,
              
              itemDetails: findItemById(it.itemId) ?? null,
              bom: it.bom ?? null,
            })),
          })),
        }));

        return {
          productionProcessId: pe.productionProcessId,
          productionProcessDetails: procDetails ?? null,
          subprocesses: procDetails ? parseSubprocessList(procDetails) : [],
          subprocessItems: subprocessArray,
        };
      }),
    };

    try {
      setLoading(true);
      const res = await axios.post(`${Base_URL}/ProductionStation/CreatePlan`, payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Production plan created");
      } else {
        toast.error("Error creating production plan");
      }
    } catch (error) {
      console.error("CreatePlan error:", error);
      toast.error("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
    console.log(payload);
  };

  // ── UI ──────────────────────────────────────────────────────

  return (
    <div className="main-content expand production-plan-wrapper">
      <Container fluid className="production-plan-container">
        <h3 className="production-plan-title">Production Plan</h3>

        <Form onSubmit={handleSubmit} className="production-plan-form">

          {/* ── Header Card ── */}
          <div className="production-plan-header-card">
            <Row>
              <Col md={3}>
                <Form.Group className="production-plan-form-group">
                  <Form.Label className="production-plan-label">Name of the Plan</Form.Label>
                  <Form.Control
                    className="production-plan-control"
                    name="planName"
                    value={form.planName}
                    onChange={handleFormChange}
                    placeholder="Plan name"
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="production-plan-form-group">
                  <Form.Label className="production-plan-label">Item Code (Top-level)</Form.Label>
                  <Form.Control
                    as="select"
                    className="production-plan-control"
                    name="itemCode"
                    value={form.itemCode}
                    onChange={handleFormChange}
                    disabled={fetchingLookups || items.length === 0}
                  >
                    <option value="">Select Item</option>
                    {serviceItems.map((itm) => (
                      <option key={itm.itemId} value={itm.itemId}>
                        {itm.itemCode} - {itm.itemName}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="production-plan-form-group">
                  <Form.Label className="production-plan-label">Production Quantity</Form.Label>
                  <Form.Control
                    className="production-plan-control"
                    name="productionQuantity"
                    value={form.productionQuantity}
                    onChange={handleFormChange}
                    placeholder="Qty"
                  />
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="production-plan-form-group">
                  <Form.Label className="production-plan-label">Unit</Form.Label>
                  <Form.Control
                    as="select"
                    className="production-plan-control"
                    name="productionQuantityUnit"
                    value={form.productionQuantityUnit}
                    onChange={handleFormChange}
                    disabled={fetchingLookups || units.length === 0}
                  >
                    <option value="">Select Unit</option>
                    {units.map((u, i) => {
                      const code = u.unitCode ?? u.code ?? u.value ?? u.id ?? u.UnitCode ?? u.unit;
                      const label = u.unitName ?? u.name ?? u.label ?? String(code);
                      return <option key={i} value={String(code)}>{label}</option>;
                    })}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group className="production-plan-form-group">
                  <Form.Label className="production-plan-label">Office Code</Form.Label>
                  <Form.Control
                    as="select"
                    className="production-plan-control"
                    name="officeCode"
                    value={form.officeCode}
                    onChange={handleFormChange}
                    disabled={fetchingLookups || offices.length === 0}
                  >
                    <option value="">Select Office</option>
                    {offices.map((o, i) => {
                      const code = o.officeCode ?? o.code ?? o.value ?? o.id ?? o.OfficeCode ?? "";
                      const name = o.officeName ?? o.name ?? o.label ?? String(code);
                      return <option key={i} value={String(code)}>{`${String(code)} - ${name}`}</option>;
                    })}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* ── Process Entries ── */}
          {processEntries.map((pe, pIdx) => {
            const proc = findProcessById(pe.productionProcessId);
            const subprocessIds = parseSubprocessList(proc || {});

            return (
              <div key={pIdx} className="production-plan-process-card">

                {/* Process selector row */}
                <Row className="align-items-end">
                  <Col md={11}>
                    <Form.Group className="production-plan-form-group">
                      <Form.Label className="production-plan-label">
                        Production Process #{pIdx + 1}
                      </Form.Label>
                      <Form.Control
                        as="select"
                        className="production-plan-control"
                        value={pe.productionProcessId}
                        onChange={(e) => onProcessSelectChange(pIdx, e.target.value)}
                        disabled={fetchingLookups || processes.length === 0}
                      >
                        <option value="">Select Process</option>
                        {processes.map((p, i) => {
                          const id = p.productionProcessId ?? p.productionprocessid ?? p.id ?? `proc-${i}`;
                          const desc = p.productionProcessDesc ?? p.productionprocessdesc ?? "";
                          return (
                            <option key={`${id}-${i}`} value={String(id)}>
                              {id}{desc ? ` - ${desc}` : ""}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={1} className="text-end">
                    {processEntries.length > 1 && (
                      <Button
                        className="production-plan-btn-remove"
                        onClick={() => removeProcessEntry(pIdx)}
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                </Row>

                {/* Process details & subprocess list */}
                <Row className="mt-2">
                  <Col md={6}>
                    <h6 className="production-plan-section-heading">Process Details</h6>
                    {proc ? (
                      <ListGroup className="production-plan-details-card">
                        <ListGroup.Item className="production-plan-list-item">
                          <strong>ID:</strong> {proc.productionProcessId}
                        </ListGroup.Item>
                        <ListGroup.Item className="production-plan-list-item">
                          <strong>Description:</strong> {proc.productionProcessDesc}
                        </ListGroup.Item>
                        <ListGroup.Item className="production-plan-list-item">
                          <strong>Organisation:</strong> {proc.organisationId}
                        </ListGroup.Item>
                      </ListGroup>
                    ) : (
                      <div className="production-plan-empty-state">No process selected</div>
                    )}
                  </Col>

                  <Col md={6}>
                    <h6 className="production-plan-section-heading">Subprocesses</h6>
                    {proc ? (
                      <ListGroup className="production-plan-details-card">
                        {subprocessIds.length === 0 ? (
                          <ListGroup.Item className="production-plan-subprocess-badge-item">
                            No subprocesses listed
                          </ListGroup.Item>
                        ) : (
                          subprocessIds.map(sp => (
                            <ListGroup.Item key={sp} className="production-plan-subprocess-badge-item">
                              {sp}
                            </ListGroup.Item>
                          ))
                        )}
                      </ListGroup>
                    ) : (
                      <div className="production-plan-empty-state">No subprocesses</div>
                    )}
                  </Col>
                </Row>

                {/* Subprocess Items section */}
                <Row className="mt-3 production-plan-subprocess-section">
                  <Col md={12}>
                    <h6 className="production-plan-section-heading">Subprocess Items</h6>

                    {subprocessIds.length === 0 ? (
                      <div className="production-plan-empty-state">No subprocesses to add items to</div>
                    ) : (
                      subprocessIds.map(subId => {
                        const sub = (pe.subprocessItems && pe.subprocessItems[subId]) || emptySubprocessEntry();

                        return (
                          <div key={subId} className="production-plan-subprocess-block">
                            <div className="production-plan-subprocess-id">{subId}</div>

                            {/* SFG Output Items */}
                            <Form.Group className="production-plan-form-group">
                              <Form.Label className="production-plan-label">SFG Output Item</Form.Label>

                              {sub.sfgOutputs?.map((sfg, sIdx) => (
                                <div key={sIdx} className="production-plan-sfg-block">

                                  {/* SFG Header Row */}
                                  <div className="production-plan-sfg-header">
                                    <span className="production-plan-sfg-tag">SFG {sIdx + 1}</span>
                                  </div>

                                  <Row className="align-items-end mb-2">
                                    <Col md={5}>
                                      <Form.Control
                                        as="select"
                                        className="production-plan-control"
                                        value={sfg.itemId}
                                       onChange={(e) =>
  updateSFGItem(pIdx, subId, sIdx, {
    itemId: e.target.value,
    items: (sfg.items || []).map(it => ({
      ...it,
      bom: null,
      bomError: null,
      bomLoading: false,
    })),
  })
}

                                      >
                                        <option value="">Select Item</option>
                                        {sfItems.map((itm) => (
                                          <option key={itm.itemId} value={itm.itemId}>
                                            {itm.itemCode} - {itm.itemName}
                                          </option>
                                        ))}
                                      </Form.Control>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Control
                                        type="number"
                                        className="production-plan-control"
                                        value={sfg.quantity}
                                        onChange={(e) =>
                                          updateSFGItem(pIdx, subId, sIdx, { quantity: e.target.value })
                                        }
                                        placeholder="Qty"
                                      />
                                    </Col>

                                    <Col md={3}>
                                      <Form.Control
                                        as="select"
                                        className="production-plan-control"
                                        value={sfg.unit}
                                        onChange={(e) =>
                                          updateSFGItem(pIdx, subId, sIdx, { unit: e.target.value })
                                        }
                                      >
                                        <option value="">Unit</option>
                                        {units.map((u, i) => {
                                          const code = u.unitCode ?? u.code ?? u.value;
                                          return <option key={i} value={code}>{code}</option>;
                                        })}
                                      </Form.Control>
                                    </Col>

                                    <Col md={1} className="text-end">
                                      {sub.sfgOutputs.length > 1 && (
                                        <Button
                                          className="production-plan-btn-remove"
                                          onClick={() => removeSFGItem(pIdx, subId, sIdx)}
                                        >
                                          −
                                        </Button>
                                      )}
                                    </Col>
                                  </Row>

                                  {/* Items inside SFG */}
                                  <div className="production-plan-items-section">
                                    <div className="production-plan-items-title">Items for this SFG</div>

                                    {(sfg.items || []).map((it, itIdx) => (
                                      <div key={itIdx} className="production-plan-item-row">
                                        <Row className="align-items-end">
                                          <Col md={5}>
                                            <Form.Control
                                              as="select"
                                              className="production-plan-control"
                                              value={it.itemId}
                                             onChange={(e) => {
  const value = e.target.value;

  updateItemInSFG(pIdx, subId, sIdx, itIdx, {
    itemId: value,
    bom: null,
  });

  fetchBOMForSFGItem(
    pIdx,
    subId,
    sIdx,
    itIdx,
    value,
    sfg.itemId
  );
}}

                                            >
                                              <option value="">Select Item</option>
                                              {items.map((itm) => (
                                                <option key={itm.itemId} value={itm.itemId}>
                                                  {itm.itemCode} - {itm.itemName}
                                                </option>
                                              ))}
                                            </Form.Control>
                                          </Col>

                                          {/* <Col md={3}>
                                            <Form.Control
                                              type="number"
                                              className="production-plan-control"
                                              value={it.quantity || ""}
                                              onChange={(e) =>
                                                updateItemInSFG(pIdx, subId, sIdx, itIdx, {
                                                  quantity: e.target.value,
                                                })
                                              }
                                              placeholder="Qty"
                                            />
                                          </Col> */}

                                          <Col md={3}>
                                            {it.bomLoading ? (
                                              <div className="production-plan-spinner-wrap">
                                                <Spinner size="sm" />
                                                <span>Loading BOM…</span>
                                              </div>
                                            ) : it.bom ? (
                                              <div className="production-plan-bom-info">
                                                <div><strong>BOM:</strong> {it.bom.bomId}</div>
                                                <div><strong>QTY Per Parent:</strong> {it.bom.quantityPerParent} {it.bom.unitCode}</div>
                                              </div>
                                            ) : it.bomError ? (
                                              <div className="production-plan-bom-error">{it.bomError}</div>
                                            ) : null}
                                          </Col>

                                          <Col md={1} className="text-end">
                                            {sfg.items.length > 1 && (
                                              <Button
                                                className="production-plan-btn-remove"
                                                onClick={() => removeItemFromSFG(pIdx, subId, sIdx, itIdx)}
                                              >
                                                −
                                              </Button>
                                            )}
                                          </Col>
                                        </Row>
                                      </div>
                                    ))}

                                    <Button
                                      className="production-plan-btn-add mt-2"
                                      size="sm"
                                      onClick={() => addItemToSFG(pIdx, subId, sIdx)}
                                    >
                                      + Add Item
                                    </Button>
                                  </div>
                                </div>
                              ))}

                              <Button
                                className="production-plan-btn-add mt-1"
                                size="sm"
                                onClick={() => addSFGItem(pIdx, subId)}
                              >
                                + Add SFG Item
                              </Button>
                            </Form.Group>

                            {/* Approx Duration */}
                            <div className="production-plan-duration-block">
                              <Row className="align-items-end">
                                <Col md={4}>
                                  <Form.Group className="production-plan-form-group mb-0">
                                    <Form.Label className="production-plan-label">
                                      Approx. Duration (Subprocess)
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      step="any"
                                      className="production-plan-control"
                                      value={sub.approxDuration}
                                      onChange={(e) =>
                                        updateSubprocessField(pIdx, subId, { approxDuration: e.target.value })
                                      }
                                      placeholder="Duration"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={3}>
                                  <Form.Group className="production-plan-form-group mb-0">
                                    <Form.Label className="production-plan-label">Unit</Form.Label>
                                    <Form.Control
                                      as="select"
                                      className="production-plan-control"
                                      value={sub.approxDurationUnit}
                                      onChange={(e) =>
                                        updateSubprocessField(pIdx, subId, { approxDurationUnit: e.target.value })
                                      }
                                    >
                                      {DURATION_UNITS.map(u => (
                                        <option key={u} value={u}>{u}</option>
                                      ))}
                                    </Form.Control>
                                  </Form.Group>
                                </Col>
                              </Row>
                            </div>

                            {/* Labour Section */}
                            <div className="production-plan-labour-section">
                              <div className="production-plan-labour-heading">Labours (Subprocess)</div>

                              {(sub.labours || []).map((lb, lIdx) => (
                                <div key={lIdx} className="production-plan-labour-row">
                                  <Row className="align-items-end">
                                    <Col md={4}>
                                      <Form.Group className="production-plan-form-group mb-0">
                                        <Form.Label className="production-plan-label">Labour Type</Form.Label>
                                        <Form.Control
                                          as="select"
                                          className="production-plan-control"
                                          value={lb.labourType}
                                          onChange={(e) =>
                                            updateLabourInSubprocess(pIdx, subId, lIdx, { labourType: e.target.value })
                                          }
                                        >
                                          {LABOUR_TYPES.map(l => (
                                            <option key={l} value={l}>{l}</option>
                                          ))}
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Group className="production-plan-form-group mb-0">
                                        <Form.Label className="production-plan-label">Labour Duration</Form.Label>
                                        <Form.Control
                                          type="number"
                                          step="any"
                                          className="production-plan-control"
                                          value={lb.labourDuration}
                                          onChange={(e) =>
                                            updateLabourInSubprocess(pIdx, subId, lIdx, { labourDuration: e.target.value })
                                          }
                                        />
                                      </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Group className="production-plan-form-group mb-0">
                                        <Form.Label className="production-plan-label">Duration Unit</Form.Label>
                                        <Form.Control
                                          as="select"
                                          className="production-plan-control"
                                          value={lb.labourDurationUnit}
                                          onChange={(e) =>
                                            updateLabourInSubprocess(pIdx, subId, lIdx, { labourDurationUnit: e.target.value })
                                          }
                                        >
                                          {LABOUR_DURATION_UNITS.map(u => (
                                            <option key={u} value={u}>{u}</option>
                                          ))}
                                        </Form.Control>
                                      </Form.Group>
                                    </Col>

                                    <Col md={1} className="text-end">
                                      {sub.labours.length > 1 && (
                                        <Button
                                          className="production-plan-btn-remove"
                                          onClick={() => removeLabourFromSubprocess(pIdx, subId, lIdx)}
                                        >
                                          −
                                        </Button>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              ))}

                              <Button
                                className="production-plan-btn-add mt-2"
                                size="sm"
                                onClick={() => addLabourToSubprocess(pIdx, subId)}
                              >
                                + Add Labour
                              </Button>
                            </div>

                          </div>
                        );
                      })
                    )}
                  </Col>
                </Row>

              </div>
            );
          })}

          {/* Process-level action buttons */}
          <div className="production-plan-actions-row">
            <Button
              type="button"
              className="production-plan-btn-primary"
              onClick={addProcessEntry}
            >
              + Add Process
            </Button>
            <Button
              type="button"
              className="production-plan-btn-secondary"
              onClick={() => setProcessEntries([emptyProcessEntry()])}
            >
              Reset Processes
            </Button>
          </div>

          {/* Submit row */}
          <div className="production-plan-submit-row">
            <Button
              type="submit"
              className="production-plan-btn-primary"
              disabled={fetchingLookups || loading}
            >
              {loading ? "Saving…" : "Save Production Plan"}
            </Button>

            {(fetchingLookups || loading) && (
              <div className="production-plan-global-spinner">
                <Spinner animation="border" size="sm" />
                <span>{loading ? "Saving plan…" : "Loading data…"}</span>
              </div>
            )}
          </div>

          {fetchError && (
            <div className="production-plan-fetch-error">{fetchError}</div>
          )}
        </Form>
      </Container>
    </div>
  );
};

export default ProductionPlan;